import { useMemo, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { DateRangePicker } from 'react-date-range';
import type { VoidFunctionComponent } from 'react';
import type { Range } from 'react-date-range';
import Button, { ButtonGroup } from '../generic/Button';
import colors from '../../theme/colors';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styles from './ModalDateRangePicker.module.less';

const RANGE_KEY = 'fossa-date-range';

const MONTHS_TO_DISPLAY = 2;

type Props = {
  startDate?: Date | string;
  endDate?: Date | string;
  show: boolean;
  onSetDateRange: (range: { startDate: Date, endDate: Date }) => void;
  onHide: () => void;
};

/**
 * Displays a modal which allows users to select a date range.
 */
const ModalDateRangePicker: VoidFunctionComponent<Props> = ({
  show,
  startDate: startDateProp,
  endDate: endDateProp,
  onHide,
  onSetDateRange,
}) => {
  const [startDate, setStartDate] = useState<Date>(moment(startDateProp || new Date()).toDate());
  const [endDate, setEndDate] = useState<Date>(moment(endDateProp || new Date()).toDate());

  const onChange = (ranges: { [RANGE_KEY]: Range }) => {
    const range = ranges[RANGE_KEY];
    if (range.startDate) {
      setStartDate(range.startDate);
    }
    if (range.endDate) {
      setEndDate(range.endDate);
    }
  };

  const ranges: Range[] = useMemo(() => [{
    key: RANGE_KEY,
    color: colors.fossaGreen,
    startDate,
    endDate,
  }], [startDate, endDate]);

  const onSubmit = () => {
    onSetDateRange({
      startDate,
      endDate,
    });
    onHide();
  };

  return (
    <Modal
      onHide={onHide}
      show={show}
      animation
    >
      <Modal.Header closeButton>
        <Modal.Title>Select a date range</Modal.Title>
      </Modal.Header>
      { /* The DateRangePicker has its own rigid styling so this modifies the modal body to better accommodate it. */ }
      <Modal.Body className={styles.modalBody}>
        <DateRangePicker
          ranges={ranges}
          months={MONTHS_TO_DISPLAY}
          maxDate={moment().endOf('day').toDate()}
          onChange={onChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={onSubmit}
          >
            Select
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDateRangePicker;
